/**
 * Insurer Search Modal Component
 *
 * 保険者検索モーダル - Insurer lookup modal
 * Allows searching and selecting insurers by insurer number
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { InsurerSearchResult } from '@/pages/main/patient/hooks';
import { useInfiniteInsurerSearch } from '@/hooks/main/useInsurerSearch';
import { convertWarekiDetail } from '@/utils/wareki';
import { AppModal, AppTable, AppSpin } from '@components/atoms';
import { useAuth } from '@shared/providers/AuthContext';

interface InsurerSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (result: InsurerSearchResult) => void;
  insuranceTypeId?: number;
  /** Current 保険者番号 from the form; modal input is pre-filled. */
  initialInsurerNumber?: string;
  /** When set, modal opens with these results (no search on open). Used when 保険者検索 returned multiple. */
  initialResults?: InsurerSearchResult[] | null;
  /** Focused after a successful insurer selection (e.g. 記号/番号 入力欄). */
  focusAfterSelectRef?: React.RefObject<{ focus?: () => void } | null>;
  /** Health insurance card lookup: exclude medical-aid-only insurer types. */
  healthInsuranceOnly?: boolean;
}

const InsurerSearchModal: React.FC<InsurerSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialInsurerNumber = '',
  initialResults = null,
  focusAfterSelectRef,
  healthInsuranceOnly = false,
}) => {
  const { clinic } = useAuth();

  const [displayResults, setDisplayResults] = useState<InsurerSearchResult[]>([]);
  const [activeInsurerNumber, setActiveInsurerNumber] = useState<string | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Use infinite query for pagination
  const {
    data: infiniteData,
    isLoading: isInfiniteLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteInsurerSearch(
    clinic?.site_type || 'medix',
    clinic?.clinic_number || '',
    activeInsurerNumber,
    { healthInsuranceOnly }
  );

  // Flatten pages from infinite query into a single data array, mapping Insurer to InsurerSearchResult
  const allData = useMemo(() => {
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap(page =>
      (page.data || []).map(
        item =>
          ({
            ...item,
            insurer_kind_name: item.insurer_type?.name ?? '',
            insurance_type_id: item.insurer_type_id ?? 1,
            has_payment_history: true,
            available_from: item.available_from,
            available_to: item.available_to,
            sub_number: item.sub_number,
          }) as InsurerSearchResult
      )
    );
  }, [infiniteData]);

  const totalCount = useMemo(() => {
    if (!infiniteData?.pages || infiniteData.pages.length === 0) return 0;
    const lastPage = infiniteData.pages[infiniteData.pages.length - 1];
    return lastPage.pagination?.total || allData.length;
  }, [infiniteData, allData.length]);

  const handleSelectResult = useCallback(
    (result: InsurerSearchResult) => {
      onSelect(result);
      onClose();
      queueMicrotask(() => focusAfterSelectRef?.current?.focus?.());
    },
    [onClose, onSelect, focusAfterSelectRef]
  );

  // When modal opens, initialize with results
  useEffect(() => {
    if (isOpen && initialResults?.length) {
      setDisplayResults(initialResults);
      setActiveInsurerNumber((initialInsurerNumber || '').trim());
    } else if (isOpen) {
      setDisplayResults([]);
      setActiveInsurerNumber(null);
    }
  }, [initialResults, initialInsurerNumber, isOpen]);

  // Handle infinite scroll: fetch next page when scrolling near the end
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const scrollElement = container.querySelector('.ant-table-body') as HTMLDivElement | null;
    if (!scrollElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      // Trigger fetch when user scrolls within 100px of the bottom
      const threshold = 100;
      if (
        scrollHeight - scrollTop - clientHeight < threshold &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const columns = [
    {
      title: '保険者番号',
      key: 'insurer_number',
      width: 100,
      render: (_: unknown, record: InsurerSearchResult & { sub_number?: string | number }) => {
        const subNumber = record.sub_number ?? '';
        return `${record.insurer_number}-${subNumber}`;
      },
    },
    {
      title: '保険者名称',
      dataIndex: 'insurer_name',
      key: 'insurer_name',
      width: 200,
    },
    {
      title: '保険者種別',
      key: 'insurer_kind_name',
      width: 150,
      render: (
        _: unknown,
        record: InsurerSearchResult & { insurer_type?: { id: number; name: string } | null }
      ) => record.insurer_type?.name ?? record.insurer_kind_name ?? '',
    },
    {
      title: '適用期間',
      key: 'period',
      width: 250,
      render: (
        _: unknown,
        record: InsurerSearchResult & { available_from?: string; available_to?: string }
      ) => {
        const formatWarekiDate = (dateStr?: string): string => {
          if (!dateStr) return '';
          const [y, m, d] = dateStr.split('-').map(Number);
          if (!y || !m || !d) return '';
          const era = convertWarekiDetail(y, m, d);
          if (!era) return '';
          return `${era}年${String(m).padStart(2, '0')}月${String(d).padStart(2, '0')}日`;
        };
        const from = formatWarekiDate(record.available_from);
        const to = formatWarekiDate(record.available_to);
        return `${from} - ${to}`;
      },
    },
  ];

  const tableData = activeInsurerNumber && allData.length > 0 ? allData : displayResults;

  return (
    <AppModal title="保険者検索" isOpen={isOpen} onClose={onClose} maxWidth="max-w-4xl">
      <AppSpin spinning={isInfiniteLoading}>
        <div className="flex flex-col gap-3" ref={tableContainerRef}>
          <div className="text-sm text-gray-600">該当件数: {totalCount}件</div>
          <p className="text-red-600">
            【保険者番号】合併、再編等により名称が変更になっている可能性があります。以下から選択してください。
          </p>
          <AppTable
            dataSource={tableData}
            columns={columns}
            rowKey="id"
            pagination={false}
            onRow={record => ({
              onClick: () => handleSelectResult(record as InsurerSearchResult),
              className: 'text-left',
            })}
          />
        </div>
      </AppSpin>
    </AppModal>
  );
};

export default InsurerSearchModal;
